import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import LayoutSingleViewFullWidth from '../uiV2/common/layout/LayoutSingleViewFullWidth'
import { MenuItems } from '../enums/MenuItems'
import Section from '../components/Section'
import personApi from '../api/person/PersonApi'
import networkApi from '../api/network/NetworkApi'
import coreSignalApi from '../api/coreSignal/CoreSignalApi'
import companyApi from '../api/company/CompanyApi'
import {
    CoreSignalJobDetail,
    CoreSignalLocationPair,
    CoreSignalJobPreviewResult,
} from '../api/coreSignal/models/FetchCoreSignalJobsDtos'
import { ResolvedPersonLiteDto } from '../api/network/models/getNetworkScopedDtos'
import { PersonDto, PersonLiteDto } from '../api/person/models/PersonDto'
import { SharedWorkHistoryLiteDto } from '../api/role/models/SharedWorkHistoryDto'
import {
    CompanyCsSearchResultDto,
    TalentFlowResultDto,
} from '../api/company/models/companyCs/CompanyCsDtos'
import Button from '../components/Button'
import Note from '../components/Note'
import { NoteType } from '../enums/NoteType'

import Modal from '../components/Modal'
import ToggleSwitch from '../components/ToggleSwitch'
import Tabs from '../components/Tabs'
import { formatPersonName } from '../utils/personUtils'
import CompanySearchModal from '../uiV2/company/search/CompanySearchModal'
import LoadingSpinner from '@/uiV2/common/loading/LoadingSpinner'

type CandidateSeedOption = {
    personId: string
    label: string
    headline?: string
    linkedInUrl?: string
    location?: string
}

type ConnectionEntry = {
    personId: string
    person: PersonLiteDto
    sharedWorkHistory: SharedWorkHistoryLiteDto[]
}

type PersonCompanyRole = {
    companyId?: string
    companyName: string
    jobTitle?: string
    companyDataProviderId?: string
}

type SelectedCompanyEntry = {
    personId: string
    personName: string
    companyName: string
    jobTitle?: string
    companyDataProviderId?: string
}

type NameSearchResult = {
    query: string
    status: 'found' | 'not_found'
    matchedPersonName?: string
}

type CompanyTableRow = {
    key: string
    companyId?: string
    companyName: string
    companyDataProviderId?: string
    movers?: number
    included: boolean
}

export default function Jobs(): React.JSX.Element {
    return (
        <LayoutSingleViewFullWidth
            activeMenuItem={MenuItems.Kit}
            className="max-w-5xl"
        >
            <JobsApiTestTab />
        </LayoutSingleViewFullWidth>
    )
}

export function JobsApiTestTab(): React.JSX.Element {
    const [searchTerm, setSearchTerm] = useState('')
    const [searchResults, setSearchResults] = useState<PersonDto[]>([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [searchError, setSearchError] = useState<string | null>(null)
    const [jobSeeker, setJobSeeker] = useState<CandidateSeedOption | null>(null)
    const [connectionsLoading, setConnectionsLoading] = useState(false)
    const [connectionsError, setConnectionsError] = useState<string | null>(
        null,
    )
    const [connections, setConnections] = useState<ConnectionEntry[]>([])
    const [selectedConnectionIds, setSelectedConnectionIds] = useState<
        Set<string>
    >(() => new Set<string>())
    const [jobTitlesFromProfileInput, setJobTitlesFromProfileInput] =
        useState('')
    const [jobTitlesMentionedOnCallInput, setJobTitlesMentionedOnCallInput] =
        useState('')
    const [systemPromptInput, setSystemPromptInput] = useState(
        `Your goal is to create a list of potential job titles that might be applicable to a job seeker searching for a job, based on the job titles that the user enters. The outputted job titles will be used in a jobs search so they must include multiple variants to ensure we capture as many relevant jobs as possible.

You may suggest job titles that represent a job at a similar level or one level above. For example for an input of "Senior Developer" a similar job title could be "Senior Engineer", and a job title one level above could be "Head of Engineering". Be creative but avoid adding job titles that will result in jobs being found that are unlikely to be relevant.

Do not suggest any job titles that are below their current job title. e.g. "Software Developer" cannot return "Junior Software Developer"

Output no more than 100 job titles.`,
    )
    const [jobTitleListLoading, setJobTitleListLoading] = useState(false)
    const [jobTitleListError, setJobTitleListError] = useState<string | null>(
        null,
    )
    const [manualJobTitlesInput, setManualJobTitlesInput] = useState('')
    const [profileCompanyRows, setProfileCompanyRows] = useState<
        CompanyTableRow[]
    >([])
    const [mentionedCompanyNamesInput, setMentionedCompanyNamesInput] =
        useState('')
    const [resolvedCompanyRows, setResolvedCompanyRows] = useState<
        CompanyTableRow[]
    >([])
    const [resolveCompanyNamesLoading, setResolveCompanyNamesLoading] =
        useState(false)
    const [resolveCompanyNamesError, setResolveCompanyNamesError] = useState<
        string | null
    >(null)
    const [talentFlowCompanyRows, setTalentFlowCompanyRows] = useState<
        CompanyTableRow[]
    >([])
    const [talentFlowCompaniesLoading, setTalentFlowCompaniesLoading] =
        useState(false)
    const [talentFlowCompaniesError, setTalentFlowCompaniesError] = useState<
        string | null
    >(null)
    const [manualCompanyRows, setManualCompanyRows] = useState<
        CompanyTableRow[]
    >([])
    const [coreSignalLoading, setCoreSignalLoading] = useState(false)
    const [coreSignalError, setCoreSignalError] = useState<string | null>(null)
    const [coreSignalJobDetails, setCoreSignalJobDetails] = useState<
        CoreSignalJobDetail[]
    >([])
    const [coreSignalSearchIds, setCoreSignalSearchIds] = useState<number[]>([])
    const [activeJobsOnly, setActiveJobsOnly] = useState(true)
    const [coreSignalPreviewLoading, setCoreSignalPreviewLoading] =
        useState(false)
    const [coreSignalPreviewError, setCoreSignalPreviewError] = useState<
        string | null
    >(null)
    const [coreSignalPreviewResults, setCoreSignalPreviewResults] = useState<
        CoreSignalJobPreviewResult[]
    >([])
    const [jobDetailModalOpen, setJobDetailModalOpen] = useState(false)
    const [jobDetailModalJobId, setJobDetailModalJobId] = useState<
        string | null
    >(null)
    const [jobDetailModalData, setJobDetailModalData] =
        useState<CoreSignalJobDetail | null>(null)
    const [jobDetailModalError, setJobDetailModalError] = useState<
        string | null
    >(null)
    const [jobDetailModalLoading, setJobDetailModalLoading] = useState(false)
    const [jobDetailModalTabIndex, setJobDetailModalTabIndex] = useState(0)
    const [manualCompanyModalOpen, setManualCompanyModalOpen] = useState(false)
    const [multiCompanyRoleMap, setMultiCompanyRoleMap] = useState<
        Record<string, PersonCompanyRole[]>
    >(() => ({}))
    const [connectionNameSearchInput, setConnectionNameSearchInput] =
        useState('')
    const [connectionNameSearchResults, setConnectionNameSearchResults] =
        useState<NameSearchResult[]>([])
    const [connectionNameSearchLoading, setConnectionNameSearchLoading] =
        useState(false)
    const [connectionNameSearchError, setConnectionNameSearchError] = useState<
        string | null
    >(null)
    const [jobLocationPairsInput, setJobLocationPairsInput] = useState('')
    const [minSalaryInput, setMinSalaryInput] = useState('')
    const [maxSalaryInput, setMaxSalaryInput] = useState('')
    const [includeMissingSalary, setIncludeMissingSalary] = useState(true)
    const lastSearchIdRef = useRef(0)
    const activeConnectionsRequestIdRef = useRef(0)
    const jobDetailRequestIdRef = useRef(0)
    const jobTitleListRequestIdRef = useRef(0)

    const availableSeedOptions = useMemo(() => {
        return searchResults.map((result) => buildCandidateSeedOption(result))
    }, [searchResults])

    const previewHighlightIds = useMemo(() => {
        return getPreviewHighlightIds(coreSignalPreviewResults, 3)
    }, [coreSignalPreviewResults])

    useEffect(() => {
        if (!jobSeeker?.location) {
            return
        }
        const parsedPairs = parseLocationPairsInput(jobSeeker.location)
        setJobLocationPairsInput(
            parsedPairs.length === 0
                ? ''
                : parsedPairs
                      .map((pair) => `${pair.city}, ${pair.country}`)
                      .join('\n'),
        )
    }, [jobSeeker])

    const handleSearch = async (event: FormEvent) => {
        event.preventDefault()
        const trimmedId = searchTerm.trim()
        if (!trimmedId) {
            lastSearchIdRef.current += 1
            activeConnectionsRequestIdRef.current += 1
            setSearchResults([])
            setSearchError(null)
            setJobSeeker(null)
            setConnections([])
            setConnectionsError(null)
            setConnectionsLoading(false)
            setSearchLoading(false)
            setSelectedConnectionIds(new Set<string>())
            setMultiCompanyRoleMap({})
            setConnectionNameSearchInput('')
            setConnectionNameSearchResults([])
            setConnectionNameSearchError(null)
            setConnectionNameSearchLoading(false)
            setProfileCompanyRows([])
            setResolvedCompanyRows([])
            setTalentFlowCompanyRows([])
            setTalentFlowCompaniesError(null)
            setTalentFlowCompaniesLoading(false)
            return
        }

        const searchId = ++lastSearchIdRef.current
        setSearchLoading(true)
        setSearchError(null)
        setSearchResults([])
        setJobSeeker(null)
        setProfileCompanyRows([])
        setResolvedCompanyRows([])
        setTalentFlowCompanyRows([])
        setTalentFlowCompaniesError(null)
        setTalentFlowCompaniesLoading(false)
        try {
            const response = await personApi.getPersonById(trimmedId)
            if (lastSearchIdRef.current !== searchId) {
                return
            }
            const person = response.person ?? null
            setSearchResults(person ? [person] : [])
            if (!person) {
                setSearchError('No person found for that ID.')
                setProfileCompanyRows([])
            } else {
                const profileJobTitles = extractProfileJobTitles(person)
                setJobTitlesFromProfileInput(profileJobTitles.join('\n'))
                setProfileCompanyRows(extractProfileCompanyRows(person))
                setResolvedCompanyRows([])
                setTalentFlowCompanyRows([])
                setTalentFlowCompaniesError(null)
                setTalentFlowCompaniesLoading(false)
                const jobSeekerInfo = buildCandidateSeedOption(person)
                setJobSeeker(jobSeekerInfo)
                void loadConnections(jobSeekerInfo)
            }
        } catch (error) {
            console.error('Failed to search person by ID', error)
            if (lastSearchIdRef.current === searchId) {
                setSearchError(
                    'There was a problem looking up that person. Please try again.',
                )
            }
        } finally {
            if (lastSearchIdRef.current === searchId) {
                setSearchLoading(false)
            }
        }
    }

    const fetchPersonLiteMap = async (
        personIds: string[],
    ): Promise<Map<string, PersonLiteDto>> => {
        if (personIds.length === 0) {
            return new Map()
        }

        const chunkSize = 25
        const persons: PersonLiteDto[] = []
        for (let i = 0; i < personIds.length; i += chunkSize) {
            const batchIds = personIds.slice(i, i + chunkSize)
            const response = await personApi.getPersonsLite({
                personIds: batchIds,
                includeCurrentJobDetails: true,
            })
            persons.push(...response.persons)
        }

        return new Map(persons.map((person) => [person.id, person]))
    }

    const loadMultiCompanyDetails = async (
        personIds: string[],
        requestId: number,
    ) => {
        const uniqueIds = Array.from(new Set(personIds))
        if (uniqueIds.length === 0) {
            return
        }

        const aggregatedDetails: Record<string, PersonCompanyRole[]> = {}
        const chunkSize = 25

        try {
            for (let i = 0; i < uniqueIds.length; i += chunkSize) {
                if (activeConnectionsRequestIdRef.current !== requestId) {
                    return
                }
                const batchIds = uniqueIds.slice(i, i + chunkSize)
                const response = await personApi.getPersonsById(batchIds)
                response.persons.forEach((person) => {
                    aggregatedDetails[person.id] =
                        extractActiveCompanyRoles(person)
                })
            }

            if (activeConnectionsRequestIdRef.current !== requestId) {
                return
            }

            setMultiCompanyRoleMap(aggregatedDetails)
        } catch (error) {
            if (activeConnectionsRequestIdRef.current === requestId) {
                console.error(
                    'Failed to load multi-company current roles',
                    error,
                )
            }
        }
    }

    const loadConnections = async (option: CandidateSeedOption) => {
        const requestId = ++activeConnectionsRequestIdRef.current
        setConnections([])
        setConnectionsError(null)
        setSelectedConnectionIds(new Set<string>())
        setConnectionsLoading(true)
        setCoreSignalJobDetails([])
        setCoreSignalSearchIds([])
        setCoreSignalError(null)
        setCoreSignalPreviewResults([])
        setCoreSignalPreviewError(null)
        setCoreSignalPreviewLoading(false)
        setJobDetailModalOpen(false)
        setJobDetailModalJobId(null)
        setJobDetailModalData(null)
        setJobDetailModalError(null)
        setJobDetailModalLoading(false)
        jobDetailRequestIdRef.current += 1
        setMultiCompanyRoleMap({})
        setConnectionNameSearchInput('')
        setConnectionNameSearchResults([])
        setConnectionNameSearchError(null)
        setConnectionNameSearchLoading(false)

        try {
            const networkRes = await networkApi.getPersonNetwork({
                personId: option.personId,
            })

            if (activeConnectionsRequestIdRef.current !== requestId) {
                return
            }

            const resolvedPersonIds = Array.from(
                new Set(
                    networkRes.resolvedPersonLites
                        .map((resolved) => resolved.personId)
                        .filter((personId) => personId !== option.personId),
                ),
            )

            if (resolvedPersonIds.length === 0) {
                setConnections([])
                return
            }

            const personMap = await fetchPersonLiteMap(resolvedPersonIds)

            if (activeConnectionsRequestIdRef.current !== requestId) {
                return
            }

            const connectionEntries = buildConnectionEntries(
                option.personId,
                networkRes.resolvedPersonLites,
                personMap,
            )
            setConnections(connectionEntries)

            const multiCompanyPersonIds = connectionEntries
                .filter(
                    (entry) => entry.person.currentJobDetails?.variousCompanies,
                )
                .map((entry) => entry.personId)

            if (multiCompanyPersonIds.length > 0) {
                void loadMultiCompanyDetails(multiCompanyPersonIds, requestId)
            }
        } catch (error) {
            if (activeConnectionsRequestIdRef.current === requestId) {
                console.error('Failed to load connections', error)
                setConnectionsError(
                    error instanceof Error
                        ? error.message
                        : 'Unable to load worked_with connections right now.',
                )
                setConnections([])
            }
        } finally {
            if (activeConnectionsRequestIdRef.current === requestId) {
                setConnectionsLoading(false)
            }
        }
    }

    const resolveSalaryFilters = (): {
        minSalary?: number
        maxSalary?: number
        error?: string
    } => {
        const trimmedMinSalary = minSalaryInput.trim()
        const trimmedMaxSalary = maxSalaryInput.trim()
        const minSalary = parseSalaryInput(trimmedMinSalary)
        const maxSalary = parseSalaryInput(trimmedMaxSalary)

        if (trimmedMinSalary && minSalary === undefined) {
            return { error: 'Enter a valid minimum salary.' }
        }

        if (trimmedMaxSalary && maxSalary === undefined) {
            return { error: 'Enter a valid maximum salary.' }
        }

        if (
            minSalary !== undefined &&
            maxSalary !== undefined &&
            minSalary > maxSalary
        ) {
            return {
                error: 'Minimum salary must be less than or equal to maximum salary.',
            }
        }

        return { minSalary, maxSalary }
    }

    const handleFetchCoreSignalJobs = async () => {
        if (!hasCompanyFilters) {
            setCoreSignalError(
                'Select at least one Talent Flow or manually added company with a data provider ID before running a job search.',
            )
            return
        }

        const locationPairs = parseLocationPairsInput(jobLocationPairsInput)
        const hasLocationInput = jobLocationPairsInput.trim().length > 0
        if (hasLocationInput && locationPairs.length === 0) {
            setCoreSignalError(
                'Enter locations as "City, Country" pairs (one per line). You can leave either side blank: "New York," or ", United Kingdom".',
            )
            return
        }

        const salaryFilters = resolveSalaryFilters()
        if (salaryFilters.error) {
            setCoreSignalError(salaryFilters.error)
            return
        }

        setCoreSignalLoading(true)
        setCoreSignalError(null)
        setCoreSignalJobDetails([])
        setCoreSignalSearchIds([])

        try {
            const jobTitlesPayload =
                manualJobTitles.length > 0 ? manualJobTitles : undefined

            const response = await coreSignalApi.fetchJobs({
                jobTitles: jobTitlesPayload,
                companyDataProviderIds,
                locationPairs:
                    locationPairs.length > 0 ? locationPairs : undefined,
                limit: 5,
                activeOnly: activeJobsOnly,
                minSalary: salaryFilters.minSalary,
                maxSalary: salaryFilters.maxSalary,
                includeMissingSalary,
            })

            setCoreSignalSearchIds(response.searchIds ?? [])
            setCoreSignalJobDetails(response.jobDetails ?? [])
            if (
                (!response.searchIds || response.searchIds.length === 0) &&
                (!response.jobDetails || response.jobDetails.length === 0)
            ) {
                setCoreSignalError('No CoreSignal jobs found for that query.')
            }
        } catch (error) {
            console.error('Failed to fetch CoreSignal jobs', error)
            setCoreSignalError(
                error instanceof Error
                    ? error.message
                    : 'Unable to fetch CoreSignal jobs.',
            )
        } finally {
            setCoreSignalLoading(false)
        }
    }

    const handleFetchCoreSignalPreview = async () => {
        if (!hasCompanyFilters) {
            setCoreSignalPreviewError(
                'Select at least one Talent Flow or manually added company with a data provider ID before running a preview search.',
            )
            setCoreSignalPreviewResults([])
            return
        }

        const locationPairs = parseLocationPairsInput(jobLocationPairsInput)
        const hasLocationInput = jobLocationPairsInput.trim().length > 0
        if (hasLocationInput && locationPairs.length === 0) {
            setCoreSignalPreviewError(
                'Enter locations as "City, Country" pairs (one per line). You can leave either side blank: "New York," or ", United Kingdom".',
            )
            setCoreSignalPreviewResults([])
            return
        }

        const salaryFilters = resolveSalaryFilters()
        if (salaryFilters.error) {
            setCoreSignalPreviewError(salaryFilters.error)
            setCoreSignalPreviewResults([])
            return
        }

        setCoreSignalPreviewLoading(true)
        setCoreSignalPreviewError(null)
        setCoreSignalPreviewResults([])

        try {
            const jobTitlesPayload =
                manualJobTitles.length > 0 ? manualJobTitles : undefined

            const response = await coreSignalApi.fetchJobsPreview({
                jobTitles: jobTitlesPayload,
                companyDataProviderIds,
                locationPairs:
                    locationPairs.length > 0 ? locationPairs : undefined,
                activeOnly: activeJobsOnly,
                minSalary: salaryFilters.minSalary,
                maxSalary: salaryFilters.maxSalary,
                includeMissingSalary,
            })

            const previewResults = response.previewResults ?? []
            setCoreSignalPreviewResults(previewResults)
            if (previewResults.length === 0) {
                setCoreSignalPreviewError(
                    'No preview results found for that query.',
                )
            }
        } catch (error) {
            console.error('Failed to fetch CoreSignal preview results', error)
            setCoreSignalPreviewError(
                error instanceof Error
                    ? error.message
                    : 'Unable to fetch CoreSignal preview results.',
            )
        } finally {
            setCoreSignalPreviewLoading(false)
        }
    }

    const hasJobTitleSourceInputs =
        jobTitlesFromProfileInput.trim().length > 0 ||
        jobTitlesMentionedOnCallInput.trim().length > 0

    const handleCreateJobTitlesList = async () => {
        const requestId = ++jobTitleListRequestIdRef.current
        const jobTitlesFromProfile = parseJobTitlesInput(
            jobTitlesFromProfileInput,
        )
        const jobTitlesMentionedOnCall = parseJobTitlesInput(
            jobTitlesMentionedOnCallInput,
        )
        const trimmedPrompt = systemPromptInput.trim()

        if (
            jobTitlesFromProfile.length === 0 &&
            jobTitlesMentionedOnCall.length === 0
        ) {
            setJobTitleListError(
                'Add at least one job title before generating a list.',
            )
            return
        }

        if (!trimmedPrompt) {
            setJobTitleListError('System prompt is required.')
            return
        }

        setJobTitleListLoading(true)
        setJobTitleListError(null)

        try {
            const response = await coreSignalApi.createJobTitlesList({
                jobTitlesFromProfile,
                jobTitlesMentionedOnCall,
                systemPrompt: trimmedPrompt,
            })

            if (jobTitleListRequestIdRef.current !== requestId) {
                return
            }

            const jobTitles = dedupeJobTitles(response.jobTitles ?? [])
            setManualJobTitlesInput(jobTitles.join('\n'))

            if (jobTitles.length === 0) {
                setJobTitleListError(
                    'No job titles returned. Try adjusting the prompt.',
                )
            }
        } catch (error) {
            if (jobTitleListRequestIdRef.current !== requestId) {
                return
            }
            console.error('Failed to create job titles list', error)
            setJobTitleListError(
                error instanceof Error
                    ? error.message
                    : 'Unable to create job titles list.',
            )
        } finally {
            if (jobTitleListRequestIdRef.current === requestId) {
                setJobTitleListLoading(false)
            }
        }
    }

    const handleToggleProfileCompanyRow = (rowKey: string) => {
        setProfileCompanyRows((rows) => toggleCompanyRows(rows, rowKey))
    }

    const handleToggleResolvedCompanyRow = (rowKey: string) => {
        setResolvedCompanyRows((rows) => toggleCompanyRows(rows, rowKey))
    }

    const handleToggleTalentFlowCompanyRow = (rowKey: string) => {
        setTalentFlowCompanyRows((rows) => toggleCompanyRows(rows, rowKey))
    }

    const handleToggleManualCompanyRow = (rowKey: string) => {
        setManualCompanyRows((rows) => toggleCompanyRows(rows, rowKey))
    }

    const handleToggleAllTalentFlowCompanies = () => {
        setTalentFlowCompanyRows((rows) => {
            if (rows.length === 0) {
                return rows
            }
            const shouldSelectAll = rows.some((row) => !row.included)
            return rows.map((row) => ({
                ...row,
                included: shouldSelectAll,
            }))
        })
    }

    const handleResolveCompanyNames = async () => {
        const companyNames = parseCompanyNamesInput(mentionedCompanyNamesInput)

        if (companyNames.length === 0) {
            setResolvedCompanyRows([])
            setResolveCompanyNamesError(null)
            return
        }

        setResolveCompanyNamesLoading(true)
        setResolveCompanyNamesError(null)

        try {
            const response = await companyApi.resolveCompanyNames({
                companyNames,
            })

            const resolvedRows = buildResolvedCompanyRows(
                companyNames,
                response.resolvedCompanyNames ?? {},
            )

            setResolvedCompanyRows(resolvedRows)

            if (resolvedRows.length === 0) {
                setResolveCompanyNamesError(
                    'No matching companies found for those names.',
                )
            }
        } catch (error) {
            console.error('Failed to resolve company names', error)
            setResolveCompanyNamesError(
                error instanceof Error
                    ? error.message
                    : 'Unable to resolve company names.',
            )
            setResolvedCompanyRows([])
        } finally {
            setResolveCompanyNamesLoading(false)
        }
    }

    const handleGetTalentFlowCompanies = () => {
        const seedCompanyIds = dedupeStrings([
            ...profileCompanyRows
                .filter((row) => row.included)
                .map((row) =>
                    (row.companyId ?? row.companyDataProviderId)?.trim(),
                )
                .filter((value): value is string => Boolean(value)),
            ...resolvedCompanyRows
                .filter((row) => row.included)
                .map((row) =>
                    (row.companyId ?? row.companyDataProviderId)?.trim(),
                )
                .filter((value): value is string => Boolean(value)),
        ])

        if (seedCompanyIds.length === 0) {
            setTalentFlowCompanyRows([])
            setTalentFlowCompaniesError(
                'Select at least one company from profile or resolved company names first.',
            )
            return
        }

        setTalentFlowCompaniesLoading(true)
        setTalentFlowCompaniesError(null)

        void (async () => {
            try {
                const response = await companyApi.getTalentFlowCompanies({
                    seedCompanyIds,
                })

                const rows = buildTalentFlowCompanyRows(response.results ?? [])
                setTalentFlowCompanyRows(rows)

                if (rows.length === 0) {
                    setTalentFlowCompaniesError(
                        'No talent flow companies found for those IDs.',
                    )
                }
            } catch (error) {
                console.error('Failed to fetch talent flow companies', error)
                setTalentFlowCompaniesError(
                    error instanceof Error
                        ? error.message
                        : 'Unable to fetch talent flow companies.',
                )
                setTalentFlowCompanyRows([])
            } finally {
                setTalentFlowCompaniesLoading(false)
            }
        })()
    }

    const handleManualCompanyConfirm = (companyIds: string[]) => {
        void companyIds
    }

    const handleManualCompanyConfirmCompanies = (
        companies: CompanyCsSearchResultDto[],
    ) => {
        if (companies.length === 0) {
            return
        }

        setManualCompanyRows((prev) => mergeManualCompanyRows(prev, companies))
        setManualCompanyModalOpen(false)
    }

    const handlePreviewJobDetailClick = (jobId: string) => {
        const requestId = ++jobDetailRequestIdRef.current
        setJobDetailModalOpen(true)
        setJobDetailModalJobId(jobId)
        setJobDetailModalData(null)
        setJobDetailModalError(null)
        setJobDetailModalLoading(true)
        setJobDetailModalTabIndex(0)

        void (async () => {
            try {
                const response = await coreSignalApi.fetchJobDetail({
                    jobId: jobId.toString(),
                })
                if (jobDetailRequestIdRef.current !== requestId) {
                    return
                }
                const detail = response.jobDetail ?? null
                if (!detail) {
                    setJobDetailModalError(
                        'No job details were returned for that ID.',
                    )
                    setJobDetailModalData(null)
                    return
                }
                setJobDetailModalData(detail)
            } catch (error) {
                if (jobDetailRequestIdRef.current !== requestId) {
                    return
                }
                console.error('Failed to fetch CoreSignal job detail', error)
                setJobDetailModalError(
                    error instanceof Error
                        ? error.message
                        : 'Unable to fetch CoreSignal job details.',
                )
            } finally {
                if (jobDetailRequestIdRef.current === requestId) {
                    setJobDetailModalLoading(false)
                }
            }
        })()
    }

    const handleCloseJobDetailModal = () => {
        setJobDetailModalOpen(false)
        setJobDetailModalJobId(null)
        setJobDetailModalData(null)
        setJobDetailModalError(null)
        setJobDetailModalLoading(false)
        setJobDetailModalTabIndex(0)
        jobDetailRequestIdRef.current += 1
    }

    const toggleConnectionSelection = (personId: string) => {
        setSelectedConnectionIds((prev) => {
            const next = new Set(prev)
            if (next.has(personId)) {
                next.delete(personId)
            } else {
                next.add(personId)
            }
            return next
        })
    }

    const handleFindConnectionsByName = async () => {
        if (!jobSeeker) {
            return
        }

        const namesToSearch = parseConnectionNamesInput(
            connectionNameSearchInput,
        )

        if (namesToSearch.length === 0) {
            setConnectionNameSearchResults([])
            setConnectionNameSearchError(null)
            return
        }

        setConnectionNameSearchLoading(true)
        setConnectionNameSearchError(null)

        try {
            const response = await networkApi.findConnectionsByNames({
                seedPersonId: jobSeeker.personId,
                names: namesToSearch,
            })

            const responsePersonIds = response.resolvedPersonLites.map(
                (resolved) => resolved.personId,
            )

            const existingPersonMap = new Map(
                connections.map((connection) => [
                    connection.personId,
                    connection.person,
                ]),
            )

            const missingPersonIds = responsePersonIds.filter(
                (personId) => !existingPersonMap.has(personId),
            )

            let fetchedPersons = new Map<string, PersonLiteDto>()
            if (missingPersonIds.length > 0) {
                fetchedPersons = await fetchPersonLiteMap(missingPersonIds)
            }

            const combinedPersonMap = new Map([
                ...existingPersonMap,
                ...fetchedPersons,
            ])

            const matchedEntries = buildConnectionEntries(
                jobSeeker.personId,
                response.resolvedPersonLites,
                combinedPersonMap,
            )

            if (matchedEntries.length > 0) {
                setConnections((prev) => {
                    const existingIds = new Set(
                        prev.map((entry) => entry.personId),
                    )
                    const entriesToAdd = matchedEntries.filter(
                        (entry) => !existingIds.has(entry.personId),
                    )
                    if (entriesToAdd.length === 0) {
                        return prev
                    }
                    return [...prev, ...entriesToAdd]
                })

                const multiCompanyPersonIds = matchedEntries
                    .filter(
                        (entry) =>
                            entry.person.currentJobDetails?.variousCompanies &&
                            !multiCompanyRoleMap[entry.personId],
                    )
                    .map((entry) => entry.personId)

                if (multiCompanyPersonIds.length > 0) {
                    void loadMultiCompanyDetails(
                        multiCompanyPersonIds,
                        activeConnectionsRequestIdRef.current,
                    )
                }
            }

            const idsToSelect = new Set<string>()
            response.results.forEach((result) => {
                result.matchedPersonIds.forEach((personId) =>
                    idsToSelect.add(personId),
                )
            })

            if (idsToSelect.size > 0) {
                setSelectedConnectionIds((prev) => {
                    const next = new Set(prev)
                    idsToSelect.forEach((id) => next.add(id))
                    return next
                })
            }

            const resultsForUi: NameSearchResult[] = response.results.map(
                (result) => {
                    if (result.matchedPersonIds.length === 0) {
                        return { query: result.query, status: 'not_found' }
                    }

                    const matchedNames = result.matchedPersonIds
                        .map((personId) => combinedPersonMap.get(personId))
                        .filter((person): person is PersonLiteDto =>
                            Boolean(person),
                        )
                        .map((person) => getPersonDisplayName(person))

                    return {
                        query: result.query,
                        status: 'found',
                        matchedPersonName:
                            matchedNames.length === 1
                                ? matchedNames[0]
                                : matchedNames.length > 1
                                  ? `${matchedNames.length} matches`
                                  : 'match',
                    }
                },
            )

            setConnectionNameSearchResults(resultsForUi)
        } catch (error) {
            console.error('Failed to find connections by name', error)
            setConnectionNameSearchError(
                'Unable to search this network by name right now.',
            )
        } finally {
            setConnectionNameSearchLoading(false)
        }
    }

    const selectedConnections = useMemo(
        () =>
            connections.filter((connection) =>
                selectedConnectionIds.has(connection.personId),
            ),
        [connections, selectedConnectionIds],
    )

    const manualJobTitles = useMemo(
        () => parseJobTitlesInput(manualJobTitlesInput),
        [manualJobTitlesInput],
    )

    const selectedCompanyEntries = useMemo<SelectedCompanyEntry[]>(() => {
        const entries: SelectedCompanyEntry[] = []

        selectedConnections.forEach((connection) => {
            const personName = getPersonDisplayName(connection.person)
            const fallbackCompanyInfo = getCurrentCompanyInfo(connection.person)

            if (connection.person.currentJobDetails?.variousCompanies) {
                const breakdown = multiCompanyRoleMap[connection.personId] ?? []
                if (breakdown.length > 0) {
                    breakdown.forEach((company) => {
                        entries.push({
                            personId: connection.personId,
                            personName,
                            companyName: company.companyName,
                            jobTitle: company.jobTitle,
                            companyDataProviderId:
                                company.companyDataProviderId,
                        })
                    })
                } else {
                    entries.push({
                        personId: connection.personId,
                        personName,
                        companyName: fallbackCompanyInfo.company,
                        jobTitle: fallbackCompanyInfo.jobTitle,
                        companyDataProviderId:
                            fallbackCompanyInfo.dataProviderId,
                    })
                }
                return
            }

            entries.push({
                personId: connection.personId,
                personName,
                companyName: fallbackCompanyInfo.company,
                jobTitle: fallbackCompanyInfo.jobTitle,
                companyDataProviderId: fallbackCompanyInfo.dataProviderId,
            })
        })

        return entries
    }, [selectedConnections, multiCompanyRoleMap])

    const uniqueCompanyEntries = useMemo(() => {
        const map = new Map<
            string,
            { companyName: string; dataProviderId?: string }
        >()

        selectedCompanyEntries.forEach((entry) => {
            if (!entry.companyName) {
                return
            }
            const key = entry.companyDataProviderId ?? entry.companyName
            if (!map.has(key)) {
                map.set(key, {
                    companyName: entry.companyName,
                    dataProviderId: entry.companyDataProviderId,
                })
            }
        })

        return Array.from(map.values())
    }, [selectedCompanyEntries])

    const companyDataProviderIds = useMemo(() => {
        const talentFlowIds = talentFlowCompanyRows
            .filter((row) => row.included)
            .map((row) => row.companyDataProviderId?.trim())
            .filter((value): value is string => Boolean(value))
        const manualIds = manualCompanyRows
            .filter((row) => row.included)
            .map((row) => row.companyDataProviderId?.trim())
            .filter((value): value is string => Boolean(value))

        return dedupeStrings([...talentFlowIds, ...manualIds])
    }, [manualCompanyRows, talentFlowCompanyRows])

    const hasCompanyFilters = useMemo(
        () => companyDataProviderIds.length > 0,
        [companyDataProviderIds],
    )
    const hasTalentFlowCompanies = talentFlowCompanyRows.length > 0
    const allTalentFlowCompaniesSelected = useMemo(
        () =>
            hasTalentFlowCompanies &&
            talentFlowCompanyRows.every((row) => row.included),
        [hasTalentFlowCompanies, talentFlowCompanyRows],
    )

    const jobDetailModalDescription = useMemo(
        () => extractJobDetailDescription(jobDetailModalData),
        [jobDetailModalData],
    )

    return (
        <>
            <div className="py-8 space-y-10">
                <Section title="Job Finder Test Harness" titleType="h1">
                    <form
                        onSubmit={handleSearch}
                        className="flex flex-col md:flex-row gap-3 items-start md:items-end"
                    >
                        <div className="w-full md:w-80 md:flex-none">
                            <label
                                htmlFor="jobs-search-term"
                                className="block text-sm font-semibold text-gray-700 mb-1 mt-4"
                            >
                                Job seeker person ID (optional)
                            </label>
                            <input
                                id="jobs-search-term"
                                type="text"
                                value={searchTerm}
                                onChange={(event) =>
                                    setSearchTerm(event.target.value)
                                }
                                className="form-input w-full"
                                placeholder="PERl63uck4ukvcskio"
                                disabled={searchLoading}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Used only to load worked_with connections and
                                prefill location. Leave blank to search with
                                manual company IDs instead.
                            </p>
                        </div>
                        <Button
                            type="submit"
                            style="primary"
                            enabled={!searchLoading}
                        >
                            {searchLoading ? 'Searching…' : 'Search'}
                        </Button>
                    </form>
                    {searchError && (
                        <div className="mt-4">
                            <Note type={NoteType.ERROR}>{searchError}</Note>
                        </div>
                    )}
                    {searchLoading && (
                        <div className="mt-6">
                            <LoadingSpinner />
                        </div>
                    )}
                    {!searchLoading && availableSeedOptions.length > 0 && (
                        <div className="mt-6 overflow-x-auto">
                            <table className="min-w-full border rounded-lg text-sm">
                                <thead className="bg-gray-50 text-left">
                                    <tr>
                                        <th className="py-2 px-3 font-semibold text-gray-700">
                                            Name
                                        </th>
                                        <th className="py-2 px-3 font-semibold text-gray-700">
                                            Headline
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {availableSeedOptions.map((option) => (
                                        <tr
                                            key={option.personId}
                                            className="border-t last:border-b"
                                        >
                                            <td className="py-2 px-3">
                                                <div className="font-medium text-gray-900">
                                                    {option.label}
                                                </div>
                                                {option.linkedInUrl && (
                                                    <a
                                                        href={
                                                            option.linkedInUrl
                                                        }
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-ctxt-blue text-xs"
                                                    >
                                                        LinkedIn
                                                    </a>
                                                )}
                                            </td>
                                            <td className="py-2 px-3 text-gray-700">
                                                {option.headline || '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Section>

                {jobSeeker && (
                    <Section
                        title="Worked With Connections"
                        titleType="h2"
                        className="bg-white rounded-lg p-4 shadow-sm"
                    >
                        <div className="mt-4 space-y-3">
                            {connectionsLoading && (
                                <div className="flex items-center gap-3 text-gray-600">
                                    <LoadingSpinner />
                                    <span>
                                        Fetching worked_with connections…
                                    </span>
                                </div>
                            )}
                            {connectionsError && (
                                <Note type={NoteType.ERROR}>
                                    {connectionsError}
                                </Note>
                            )}
                            {!connectionsLoading &&
                                !connectionsError &&
                                connections.length === 0 && (
                                    <Note type={NoteType.INFO}>
                                        No worked_with connections found for
                                        this person yet.
                                    </Note>
                                )}
                            {!connectionsLoading && connections.length > 0 && (
                                <>
                                    <div className="mt-6 border-t pt-4">
                                        <label
                                            htmlFor="jobs-connection-name-search"
                                            className="block text-sm font-semibold text-gray-700 mb-1"
                                        >
                                            Find and select connections by name
                                        </label>
                                        <textarea
                                            id="jobs-connection-name-search"
                                            className="form-input w-full"
                                            rows={3}
                                            value={connectionNameSearchInput}
                                            onChange={(event) =>
                                                setConnectionNameSearchInput(
                                                    event.target.value,
                                                )
                                            }
                                            placeholder="e.g., Jane Doe, Alex Johnson"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Enter names separated by commas or
                                            new lines. Matches are auto-selected
                                            to include their companies.
                                        </p>
                                        <div className="mt-3">
                                            <Button
                                                type="button"
                                                style="secondary"
                                                enabled={Boolean(
                                                    connectionNameSearchInput.trim() &&
                                                    !connectionNameSearchLoading,
                                                )}
                                                onClick={() => {
                                                    void handleFindConnectionsByName()
                                                }}
                                            >
                                                {connectionNameSearchLoading
                                                    ? 'Searching…'
                                                    : 'Find & select'}
                                            </Button>
                                        </div>
                                        {connectionNameSearchError && (
                                            <div className="mt-2">
                                                <Note type={NoteType.ERROR}>
                                                    {connectionNameSearchError}
                                                </Note>
                                            </div>
                                        )}
                                        {connectionNameSearchResults.length >
                                            0 && (
                                            <div className="mt-3 space-y-1">
                                                {connectionNameSearchResults.map(
                                                    (result, index) => (
                                                        <div
                                                            key={`${result.query}-${index}`}
                                                            className={`text-sm ${
                                                                result.status ===
                                                                'found'
                                                                    ? 'text-green-700'
                                                                    : 'text-red-700'
                                                            }`}
                                                        >
                                                            <span className="font-semibold">
                                                                {result.query}
                                                            </span>{' '}
                                                            {result.status ===
                                                            'found'
                                                                ? '— Selected!'
                                                                : '— Not found in this network.'}
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-sm border rounded-lg">
                                            <thead className="bg-gray-50 text-left">
                                                <tr>
                                                    <th className="py-2 px-3 text-xs font-semibold text-gray-500">
                                                        Select
                                                    </th>
                                                    <th className="py-2 px-3 text-xs font-semibold text-gray-500">
                                                        Person
                                                    </th>
                                                    <th className="py-2 px-3 text-xs font-semibold text-gray-500">
                                                        Current role
                                                    </th>
                                                    <th className="py-2 px-3 text-xs font-semibold text-gray-500">
                                                        Shared experiences
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {connections.map(
                                                    (connection) => {
                                                        const personName =
                                                            getPersonDisplayName(
                                                                connection.person,
                                                            )
                                                        const {
                                                            company,
                                                            jobTitle,
                                                        } =
                                                            getCurrentCompanyInfo(
                                                                connection.person,
                                                            )
                                                        const isSelected =
                                                            selectedConnectionIds.has(
                                                                connection.personId,
                                                            )
                                                        return (
                                                            <tr
                                                                key={
                                                                    connection.personId
                                                                }
                                                                className="border-t last:border-b"
                                                            >
                                                                <td className="py-2 px-3">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={
                                                                            isSelected
                                                                        }
                                                                        onChange={() =>
                                                                            toggleConnectionSelection(
                                                                                connection.personId,
                                                                            )
                                                                        }
                                                                    />
                                                                </td>
                                                                <td className="py-2 px-3 align-top">
                                                                    <div className="font-semibold text-gray-900">
                                                                        {
                                                                            personName
                                                                        }
                                                                    </div>
                                                                    {connection
                                                                        .person
                                                                        .linkedInUrl && (
                                                                        <a
                                                                            href={
                                                                                connection
                                                                                    .person
                                                                                    .linkedInUrl
                                                                            }
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            className="text-ctxt-blue text-xs"
                                                                        >
                                                                            LinkedIn
                                                                        </a>
                                                                    )}
                                                                </td>
                                                                <td className="py-2 px-3 text-gray-700">
                                                                    <div>
                                                                        {jobTitle ||
                                                                            'Role unknown'}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        {
                                                                            company
                                                                        }
                                                                    </div>
                                                                </td>
                                                                <td className="py-2 px-3 text-gray-700">
                                                                    {
                                                                        connection
                                                                            .sharedWorkHistory
                                                                            .length
                                                                    }{' '}
                                                                    {connection
                                                                        .sharedWorkHistory
                                                                        .length ===
                                                                    1
                                                                        ? 'overlap'
                                                                        : 'overlaps'}
                                                                </td>
                                                            </tr>
                                                        )
                                                    },
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    </Section>
                )}

                <Section
                    title="Job search filters"
                    titleType="h2"
                    className="bg-white rounded-lg p-4 shadow-sm"
                >
                    <p className="text-gray-700 mb-3">
                        {selectedConnections.length} person
                        {selectedConnections.length === 1 ? '' : 's'} selected.
                    </p>
                    {selectedConnections.length === 0 && (
                        <Note type={NoteType.INFO}>
                            No connections selected. Provide company provider
                            IDs below to search without a job seeker.
                        </Note>
                    )}
                    <ul className="space-y-2">
                        {selectedCompanyEntries.map((entry, index) => (
                            <li
                                key={`${entry.personId}-${entry.companyName}-${index}`}
                            >
                                <span className="font-semibold text-gray-900">
                                    {entry.personName}
                                </span>{' '}
                                — {entry.companyName}
                                {entry.jobTitle && (
                                    <span className="text-gray-600">{` (${entry.jobTitle})`}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                    <div className="mt-4">
                        <p className="text-sm font-semibold text-gray-700 mb-2">
                            Unique companies
                        </p>
                        <div className="flex flex-wrap gap-3">
                            {uniqueCompanyEntries.map((company) => (
                                <div
                                    key={
                                        company.dataProviderId ??
                                        company.companyName
                                    }
                                    className="px-3 py-2 bg-gray-50 rounded-lg border text-xs text-gray-800"
                                >
                                    <div className="font-semibold text-gray-900">
                                        {company.companyName}
                                    </div>
                                    <div className="text-[11px] text-gray-600">
                                        {company.dataProviderId
                                            ? `Data provider ID: ${company.dataProviderId}`
                                            : 'No data provider ID'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mt-6 space-y-4">
                        <div>
                            <label
                                htmlFor="jobs-location-pairs-input"
                                className="block text-sm font-semibold text-gray-700 mb-1"
                            >
                                Job locations (optional)
                            </label>
                            <textarea
                                id="jobs-location-pairs-input"
                                className="form-input w-full"
                                value={jobLocationPairsInput}
                                onChange={(event) =>
                                    setJobLocationPairsInput(event.target.value)
                                }
                                placeholder={
                                    jobSeeker?.location ??
                                    'Manchester, United Kingdom\nParis, France\nNew York,'
                                }
                                rows={2}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Enter one city + country per line (e.g.
                                "Manchester, United Kingdom"). You can leave
                                either side blank: "New York," or ", United
                                Kingdom". Use country names (not state
                                abbreviations). Leave blank to skip location
                                filtering.
                            </p>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <p className="text-sm font-semibold text-gray-700 mb-3">
                                Salary range (optional)
                            </p>
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                    <label
                                        htmlFor="jobs-min-salary"
                                        className="block text-xs font-semibold text-gray-600 mb-1"
                                    >
                                        minimum salary (GBP)
                                    </label>
                                    <input
                                        id="jobs-min-salary"
                                        type="number"
                                        min="0"
                                        step="1"
                                        className="form-input w-full"
                                        value={minSalaryInput}
                                        onChange={(event) =>
                                            setMinSalaryInput(
                                                event.target.value,
                                            )
                                        }
                                        placeholder="30000"
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="jobs-max-salary"
                                        className="block text-xs font-semibold text-gray-600 mb-1"
                                    >
                                        maximum salary (GBP)
                                    </label>
                                    <input
                                        id="jobs-max-salary"
                                        type="number"
                                        min="0"
                                        step="1"
                                        className="form-input w-full"
                                        value={maxSalaryInput}
                                        onChange={(event) =>
                                            setMaxSalaryInput(
                                                event.target.value,
                                            )
                                        }
                                        placeholder="80000"
                                    />
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                Leave blank to skip salary filtering.
                            </p>
                            <div className="mt-3">
                                <ToggleSwitch
                                    checked={includeMissingSalary}
                                    onChange={setIncludeMissingSalary}
                                    label="Include jobs without salary data"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Applies when a min or max salary is set.
                                </p>
                            </div>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <p className="text-sm font-semibold text-gray-700 mb-3">
                                Add job title(s) (optional)
                            </p>
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
                                <div className="w-full space-y-3 lg:w-96 lg:flex-none">
                                    <div>
                                        <label
                                            htmlFor="jobs-job-titles-profile"
                                            className="block text-xs font-semibold text-gray-600 mb-1"
                                        >
                                            job titles from profile
                                        </label>
                                        <p className="text-[11px] text-gray-500 mb-1">
                                            current and up to 3 years old
                                        </p>
                                        <textarea
                                            id="jobs-job-titles-profile"
                                            value={jobTitlesFromProfileInput}
                                            onChange={(event) =>
                                                setJobTitlesFromProfileInput(
                                                    event.target.value,
                                                )
                                            }
                                            className="form-input w-full"
                                            placeholder="Enter roles separated by commas or new lines"
                                            rows={2}
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="jobs-job-titles-call"
                                            className="block text-xs font-semibold text-gray-600 mb-1"
                                        >
                                            job titles mentioned on the call
                                        </label>
                                        <textarea
                                            id="jobs-job-titles-call"
                                            value={
                                                jobTitlesMentionedOnCallInput
                                            }
                                            onChange={(event) =>
                                                setJobTitlesMentionedOnCallInput(
                                                    event.target.value,
                                                )
                                            }
                                            className="form-input w-full"
                                            placeholder="Enter roles separated by commas or new lines"
                                            rows={2}
                                        />
                                    </div>
                                </div>
                                <div className="flex w-full flex-col gap-2 lg:flex-1">
                                    <label
                                        htmlFor="jobs-system-prompt"
                                        className="block text-xs font-semibold text-gray-600"
                                    >
                                        system prompt
                                    </label>
                                    <textarea
                                        id="jobs-system-prompt"
                                        value={systemPromptInput}
                                        onChange={(event) =>
                                            setSystemPromptInput(
                                                event.target.value,
                                            )
                                        }
                                        className="form-input w-full h-24"
                                        placeholder="Enter a prompt"
                                        rows={4}
                                    />
                                    <Button
                                        type="button"
                                        style="secondary"
                                        enabled={
                                            hasJobTitleSourceInputs &&
                                            !jobTitleListLoading
                                        }
                                        onClick={() => {
                                            void handleCreateJobTitlesList()
                                        }}
                                    >
                                        {jobTitleListLoading
                                            ? 'Generating…'
                                            : 'create job titles list -->'}
                                    </Button>
                                    {jobTitleListLoading && (
                                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                                            <LoadingSpinner />
                                            Generating job titles…
                                        </div>
                                    )}
                                    {jobTitleListError && (
                                        <Note type={NoteType.ERROR}>
                                            {jobTitleListError}
                                        </Note>
                                    )}
                                </div>
                                <div className="w-full lg:w-96 lg:flex-none">
                                    <label
                                        htmlFor="jobs-manual-job-title"
                                        className="block text-xs font-semibold text-gray-600 mb-1"
                                    >
                                        job titles list
                                    </label>
                                    <textarea
                                        id="jobs-manual-job-title"
                                        value={manualJobTitlesInput}
                                        onChange={(event) =>
                                            setManualJobTitlesInput(
                                                event.target.value,
                                            )
                                        }
                                        className="form-input w-full"
                                        placeholder="Enter roles separated by commas or new lines"
                                        rows={4}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Example: &quot;Head of Talent,
                                        Recruiting Manager&quot;. Leave blank to
                                        search by company filters only.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <p className="text-sm font-semibold text-gray-700 mb-3">
                                Company filters
                            </p>
                            <div className="grid gap-4 lg:grid-cols-3 lg:grid-rows-[auto_auto]">
                                <div className="space-y-2 lg:col-start-1 lg:row-start-1">
                                    <div>
                                        <p className="text-xs font-semibold text-gray-600">
                                            Companies from profile
                                        </p>
                                        <p className="text-[11px] text-gray-500">
                                            Auto-populated from work history
                                            when a data_provider_id is
                                            available.
                                        </p>
                                    </div>
                                    <CompanyTable
                                        rows={profileCompanyRows}
                                        onToggleRow={
                                            handleToggleProfileCompanyRow
                                        }
                                        emptyLabel="No profile companies with data provider IDs yet."
                                    />
                                </div>
                                <div
                                    className="hidden lg:block lg:col-start-2 lg:row-start-1"
                                    aria-hidden="true"
                                />
                                <div className="space-y-2 lg:col-start-1 lg:row-start-2">
                                    <label
                                        htmlFor="jobs-mentioned-company-names"
                                        className="block text-xs font-semibold text-gray-600 mb-1"
                                    >
                                        company names mentioned as prior work
                                        places
                                    </label>
                                    <textarea
                                        id="jobs-mentioned-company-names"
                                        value={mentionedCompanyNamesInput}
                                        onChange={(event) =>
                                            setMentionedCompanyNamesInput(
                                                event.target.value,
                                            )
                                        }
                                        className="form-input w-full"
                                        placeholder="Enter company names separated by commas or new lines"
                                        rows={4}
                                    />
                                </div>
                                <div className="space-y-2 lg:col-start-2 lg:row-start-2">
                                    <div>
                                        <p className="text-xs font-semibold text-gray-600">
                                            Resolved company names
                                        </p>
                                    </div>
                                    <CompanyTable
                                        rows={resolvedCompanyRows}
                                        onToggleRow={
                                            handleToggleResolvedCompanyRow
                                        }
                                        emptyLabel="No resolved companies yet."
                                    />
                                    <Button
                                        type="button"
                                        style="secondary"
                                        onClick={handleResolveCompanyNames}
                                        enabled={!resolveCompanyNamesLoading}
                                    >
                                        {resolveCompanyNamesLoading
                                            ? 'Resolving…'
                                            : 'Resolve company names'}
                                    </Button>
                                    {resolveCompanyNamesError && (
                                        <div className="mt-2">
                                            <Note type={NoteType.ERROR}>
                                                {resolveCompanyNamesError}
                                            </Note>
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-4 lg:col-start-3 lg:row-span-2">
                                    <div className="space-y-2">
                                        <div className="flex items-start justify-between gap-2">
                                            <p className="text-xs font-semibold text-gray-600">
                                                Talent Flow companies
                                            </p>
                                            <Button
                                                type="button"
                                                style="secondary"
                                                size="small"
                                                onClick={
                                                    handleToggleAllTalentFlowCompanies
                                                }
                                                enabled={hasTalentFlowCompanies}
                                            >
                                                {allTalentFlowCompaniesSelected
                                                    ? 'Deselect all'
                                                    : 'Select all'}
                                            </Button>
                                        </div>
                                        <CompanyTable
                                            rows={talentFlowCompanyRows}
                                            onToggleRow={
                                                handleToggleTalentFlowCompanyRow
                                            }
                                            emptyLabel="No Talent Flow companies yet."
                                            showMovers
                                        />
                                        <Button
                                            type="button"
                                            style="secondary"
                                            onClick={
                                                handleGetTalentFlowCompanies
                                            }
                                            enabled={
                                                !talentFlowCompaniesLoading
                                            }
                                        >
                                            {talentFlowCompaniesLoading
                                                ? 'Fetching…'
                                                : 'Get Talent Flow Companies'}
                                        </Button>
                                        {talentFlowCompaniesError && (
                                            <div className="mt-2">
                                                <Note type={NoteType.ERROR}>
                                                    {talentFlowCompaniesError}
                                                </Note>
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-start justify-between gap-2">
                                            <p className="text-xs font-semibold text-gray-600">
                                                Manually added companies
                                            </p>
                                            <Button
                                                type="button"
                                                style="secondary"
                                                size="small"
                                                onClick={() =>
                                                    setManualCompanyModalOpen(
                                                        true,
                                                    )
                                                }
                                            >
                                                Find companies
                                            </Button>
                                        </div>
                                        <CompanyTable
                                            rows={manualCompanyRows}
                                            onToggleRow={
                                                handleToggleManualCompanyRow
                                            }
                                            emptyLabel="No manual companies yet."
                                        />
                                        <p className="text-[11px] text-gray-500">
                                            Use the company finder to add extra
                                            companies outside of Talent Flow.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-3">
                            <ToggleSwitch
                                checked={activeJobsOnly}
                                onChange={setActiveJobsOnly}
                                label="Only include active jobs"
                            />
                            <p className="text-xs text-gray-500">
                                Toggle off to include inactive or closed roles
                                in the results.
                            </p>
                        </div>
                        <div className="mt-3 flex flex-col gap-3">
                            <div className="flex flex-col gap-3 md:flex-row">
                                <Button
                                    type="button"
                                    style="secondary"
                                    enabled={
                                        !coreSignalLoading && hasCompanyFilters
                                    }
                                    onClick={() => {
                                        void handleFetchCoreSignalJobs()
                                    }}
                                >
                                    {coreSignalLoading
                                        ? 'Fetching…'
                                        : 'Fetch CoreSignal jobs'}
                                </Button>
                                <Button
                                    type="button"
                                    style="secondary"
                                    enabled={
                                        !coreSignalPreviewLoading &&
                                        hasCompanyFilters
                                    }
                                    onClick={() => {
                                        void handleFetchCoreSignalPreview()
                                    }}
                                >
                                    {coreSignalPreviewLoading
                                        ? 'Fetching preview…'
                                        : 'Preview CoreSignal jobs (100 max)'}
                                </Button>
                            </div>
                            {coreSignalSearchIds.length > 0 && (
                                <Note type={NoteType.INFO}>
                                    Found {coreSignalSearchIds.length} jobs.
                                    Showing details for the top 5 matches.
                                </Note>
                            )}
                            {coreSignalError && (
                                <Note type={NoteType.ERROR}>
                                    {coreSignalError}
                                </Note>
                            )}
                            {coreSignalPreviewError && (
                                <Note type={NoteType.ERROR}>
                                    {coreSignalPreviewError}
                                </Note>
                            )}
                            {coreSignalPreviewLoading && (
                                <div className="flex items-center gap-2 text-gray-600 text-sm">
                                    <LoadingSpinner />
                                </div>
                            )}
                            {coreSignalJobDetails.length > 0 && (
                                <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-5">
                                    <p className="font-semibold text-gray-900">
                                        Showing details for{' '}
                                        {Math.min(
                                            coreSignalJobDetails.length,
                                            5,
                                        )}{' '}
                                        CoreSignal match
                                        {coreSignalJobDetails.length > 1
                                            ? 'es'
                                            : ''}
                                    </p>
                                    <div className="space-y-6">
                                        {coreSignalJobDetails.map(
                                            (jobDetail, index) => (
                                                <div
                                                    key={
                                                        jobDetail.id ??
                                                        jobDetail.job_id ??
                                                        index
                                                    }
                                                    className="space-y-3 border-t border-gray-200 pt-4 first:border-t-0 first:pt-0"
                                                >
                                                    <div>
                                                        <p className="font-semibold text-gray-900">
                                                            {jobDetail.title ??
                                                                'Untitled role'}{' '}
                                                            <span className="text-xs text-gray-500">
                                                                #{index + 1}
                                                            </span>
                                                        </p>
                                                        <p className="text-gray-600">
                                                            {jobDetail.company_name ??
                                                                'Unknown company'}
                                                        </p>
                                                    </div>
                                                    <div className="grid gap-1 text-gray-700">
                                                        <span>
                                                            <strong>ID:</strong>{' '}
                                                            {jobDetail.id ??
                                                                jobDetail.job_id ??
                                                                'N/A'}
                                                        </span>
                                                        <span>
                                                            <strong>
                                                                Status:
                                                            </strong>{' '}
                                                            {jobDetail.status ??
                                                                'N/A'}
                                                        </span>
                                                        <span>
                                                            <strong>
                                                                Created:
                                                            </strong>{' '}
                                                            {jobDetail.created_at ??
                                                                'N/A'}
                                                        </span>
                                                        <span>
                                                            <strong>
                                                                Updated:
                                                            </strong>{' '}
                                                            {jobDetail.updated_at ??
                                                                'N/A'}
                                                        </span>
                                                        <span>
                                                            <strong>
                                                                Location:
                                                            </strong>{' '}
                                                            {[
                                                                jobDetail.city,
                                                                jobDetail.state,
                                                                jobDetail.country,
                                                            ]
                                                                .filter(Boolean)
                                                                .join(', ') ||
                                                                'N/A'}
                                                        </span>
                                                        <span>
                                                            <strong>
                                                                Employment type:
                                                            </strong>{' '}
                                                            {jobDetail.employment_type ??
                                                                'N/A'}
                                                        </span>
                                                    </div>
                                                    {jobDetail.description && (
                                                        <p className="text-gray-700 whitespace-pre-wrap">
                                                            {
                                                                jobDetail.description
                                                            }
                                                        </p>
                                                    )}
                                                    {jobDetail.external_url && (
                                                        <a
                                                            href={
                                                                jobDetail.external_url
                                                            }
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="text-ctxt-blue text-sm"
                                                        >
                                                            External listing
                                                        </a>
                                                    )}
                                                    {Array.isArray(
                                                        jobDetail.job_sources,
                                                    ) &&
                                                        jobDetail.job_sources
                                                            .length > 0 && (
                                                            <div>
                                                                <p className="font-semibold text-gray-800 text-sm">
                                                                    Job sources
                                                                </p>
                                                                <ul className="mt-1 space-y-1 text-xs text-gray-700">
                                                                    {jobDetail.job_sources.map(
                                                                        (
                                                                            source: any,
                                                                        ) => (
                                                                            <li
                                                                                key={
                                                                                    source.source_id ??
                                                                                    source.url ??
                                                                                    `${index}-${source.source}`
                                                                                }
                                                                            >
                                                                                <span className="font-medium">
                                                                                    {source.source ??
                                                                                        'Unknown'}
                                                                                </span>
                                                                                {source.url && (
                                                                                    <>
                                                                                        {' '}
                                                                                        –{' '}
                                                                                        <a
                                                                                            href={
                                                                                                source.url
                                                                                            }
                                                                                            target="_blank"
                                                                                            rel="noreferrer"
                                                                                            className="text-ctxt-blue"
                                                                                        >
                                                                                            View
                                                                                        </a>
                                                                                    </>
                                                                                )}
                                                                            </li>
                                                                        ),
                                                                    )}
                                                                </ul>
                                                            </div>
                                                        )}
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            )}
                            {coreSignalPreviewResults.length > 0 && (
                                <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-4">
                                    <p className="font-semibold text-gray-900">
                                        Preview results (sorted by relevance) —
                                        {` ${coreSignalPreviewResults.length}`}{' '}
                                        roles
                                    </p>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full border rounded-lg text-xs">
                                            <thead className="bg-gray-100 text-left">
                                                <tr>
                                                    <th className="py-2 px-3 font-semibold text-gray-600">
                                                        Score
                                                    </th>
                                                    <th className="py-2 px-3 font-semibold text-gray-600">
                                                        Title
                                                    </th>
                                                    <th className="py-2 px-3 font-semibold text-gray-600">
                                                        Company
                                                    </th>
                                                    <th className="py-2 px-3 font-semibold text-gray-600">
                                                        Location
                                                    </th>
                                                    <th className="py-2 px-3 font-semibold text-gray-600">
                                                        Created
                                                    </th>
                                                    <th className="py-2 px-3 font-semibold text-gray-600">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {coreSignalPreviewResults.map(
                                                    (result) => {
                                                        const isHighlighted =
                                                            previewHighlightIds.has(
                                                                result.id,
                                                            )
                                                        return (
                                                            <tr
                                                                key={result.id}
                                                                className={`border-t last:border-b ${
                                                                    isHighlighted
                                                                        ? 'bg-ctxt-blue/10'
                                                                        : ''
                                                                }`}
                                                            >
                                                                <td className="py-2 px-3 align-top">
                                                                    {typeof result.score ===
                                                                    'number'
                                                                        ? result.score.toFixed(
                                                                              2,
                                                                          )
                                                                        : '—'}
                                                                </td>
                                                                <td className="py-2 px-3 align-top">
                                                                    <div className="font-semibold text-gray-900">
                                                                        {result.title ??
                                                                            'Untitled role'}
                                                                    </div>
                                                                    <div className="text-[11px] text-gray-500">
                                                                        ID:{' '}
                                                                        {
                                                                            result.id
                                                                        }
                                                                    </div>
                                                                </td>
                                                                <td className="py-2 px-3 align-top text-gray-700">
                                                                    {result.companyName ??
                                                                        'Unknown company'}
                                                                </td>
                                                                <td className="py-2 px-3 align-top text-gray-700">
                                                                    {result.location ??
                                                                        '—'}
                                                                </td>
                                                                <td className="py-2 px-3 align-top text-gray-700">
                                                                    {result.createdAt ??
                                                                        '—'}
                                                                </td>
                                                                <td className="py-2 px-3 align-top text-gray-700">
                                                                    <Button
                                                                        type="button"
                                                                        style="secondary"
                                                                        size="small"
                                                                        onClick={() => {
                                                                            handlePreviewJobDetailClick(
                                                                                result.id,
                                                                            )
                                                                        }}
                                                                    >
                                                                        Get Job
                                                                        Details
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        )
                                                    },
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Section>
            </div>
            {jobDetailModalOpen && (
                <Modal
                    title="CoreSignal Job Details"
                    onClose={handleCloseJobDetailModal}
                    size="large"
                >
                    <div className="space-y-4 text-sm">
                        <div className="text-gray-600">
                            <strong>Job ID:</strong>{' '}
                            <span className="font-mono text-gray-900">
                                {jobDetailModalJobId}
                            </span>
                        </div>
                        {jobDetailModalLoading && (
                            <div className="flex items-center gap-2 text-gray-700">
                                <LoadingSpinner />
                                <span>Fetching job details…</span>
                            </div>
                        )}
                        {jobDetailModalError && (
                            <Note type={NoteType.ERROR}>
                                {jobDetailModalError}
                            </Note>
                        )}
                        {!jobDetailModalLoading &&
                            !jobDetailModalError &&
                            jobDetailModalData && (
                                <Tabs
                                    activeIndex={jobDetailModalTabIndex}
                                    onTabChange={setJobDetailModalTabIndex}
                                    tabs={[
                                        {
                                            label: 'Summary',
                                            content: (
                                                <div className="space-y-3">
                                                    <div className="space-y-1 text-gray-800">
                                                        <div>
                                                            <strong>
                                                                Title:
                                                            </strong>{' '}
                                                            {jobDetailModalData.title ??
                                                                jobDetailModalData.job_title ??
                                                                jobDetailModalData
                                                                    ?.job
                                                                    ?.title ??
                                                                'Untitled role'}
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                Company:
                                                            </strong>{' '}
                                                            {jobDetailModalData.company_name ??
                                                                jobDetailModalData
                                                                    ?.company
                                                                    ?.name ??
                                                                jobDetailModalData
                                                                    ?.job
                                                                    ?.company_name ??
                                                                'Unknown company'}
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                Location:
                                                            </strong>{' '}
                                                            {jobDetailModalData.location ??
                                                                ([
                                                                    jobDetailModalData.city,
                                                                    jobDetailModalData.state,
                                                                    jobDetailModalData.country,
                                                                ]
                                                                    .filter(
                                                                        Boolean,
                                                                    )
                                                                    .join(
                                                                        ', ',
                                                                    ) ||
                                                                    '—')}
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                Created:
                                                            </strong>{' '}
                                                            {jobDetailModalData.created_at ??
                                                                jobDetailModalData
                                                                    ?.job
                                                                    ?.created_at ??
                                                                '—'}
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                Updated:
                                                            </strong>{' '}
                                                            {jobDetailModalData.updated_at ??
                                                                jobDetailModalData
                                                                    ?.job
                                                                    ?.updated_at ??
                                                                '—'}
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                Status:
                                                            </strong>{' '}
                                                            {jobDetailModalData.status ??
                                                                '—'}
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                Salary:
                                                            </strong>{' '}
                                                            {jobDetailModalData
                                                                .salary
                                                                ?.length ? (
                                                                <pre className="mt-1 whitespace-pre-wrap rounded bg-gray-50 p-2 text-xs text-gray-700">
                                                                    {JSON.stringify(
                                                                        jobDetailModalData.salary,
                                                                        null,
                                                                        2,
                                                                    )}
                                                                </pre>
                                                            ) : (
                                                                '—'
                                                            )}
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                Accepts remote:
                                                            </strong>{' '}
                                                            {jobDetailModalData.accepts_remote ===
                                                                null ||
                                                            jobDetailModalData.accepts_remote ===
                                                                undefined
                                                                ? '—'
                                                                : jobDetailModalData.accepts_remote
                                                                  ? 'Yes'
                                                                  : 'No'}
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                Benefits:
                                                            </strong>{' '}
                                                            {jobDetailModalData
                                                                .benefits
                                                                ?.length
                                                                ? jobDetailModalData.benefits.join(
                                                                      ', ',
                                                                  )
                                                                : '—'}
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                External URL:
                                                            </strong>{' '}
                                                            {jobDetailModalData.external_url ? (
                                                                <a
                                                                    className="text-blue-600 underline break-all"
                                                                    href={
                                                                        jobDetailModalData.external_url
                                                                    }
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                >
                                                                    {
                                                                        jobDetailModalData.external_url
                                                                    }
                                                                </a>
                                                            ) : (
                                                                '—'
                                                            )}
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                Recruiter:
                                                            </strong>
                                                            {jobDetailModalData.recruiter ? (
                                                                <pre className="mt-1 whitespace-pre-wrap rounded bg-gray-50 p-2 text-xs text-gray-700">
                                                                    {JSON.stringify(
                                                                        jobDetailModalData.recruiter,
                                                                        null,
                                                                        2,
                                                                    )}
                                                                </pre>
                                                            ) : (
                                                                <span className="ml-1">
                                                                    —
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                Job sources:
                                                            </strong>
                                                            {jobDetailModalData
                                                                .job_sources
                                                                ?.length ? (
                                                                <div className="mt-2 space-y-2">
                                                                    {jobDetailModalData.job_sources.map(
                                                                        (
                                                                            source,
                                                                            index,
                                                                        ) => (
                                                                            <pre
                                                                                key={`${source.source}-${source.source_id}-${index}`}
                                                                                className="whitespace-pre-wrap rounded bg-gray-50 p-2 text-xs text-gray-700"
                                                                            >
                                                                                {JSON.stringify(
                                                                                    source,
                                                                                    null,
                                                                                    2,
                                                                                )}
                                                                            </pre>
                                                                        ),
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className="ml-1">
                                                                    —
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {jobDetailModalDescription && (
                                                        <div>
                                                            <p className="font-semibold text-gray-900">
                                                                Description
                                                            </p>
                                                            <p className="text-gray-700 whitespace-pre-wrap">
                                                                {
                                                                    jobDetailModalDescription
                                                                }
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            ),
                                        },
                                        {
                                            label: 'JSON',
                                            content: (
                                                <pre className="max-h-[60vh] overflow-auto whitespace-pre rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800">
                                                    {JSON.stringify(
                                                        jobDetailModalData,
                                                        null,
                                                        2,
                                                    )}
                                                </pre>
                                            ),
                                        },
                                    ]}
                                />
                            )}
                    </div>
                </Modal>
            )}
            {manualCompanyModalOpen && (
                <CompanySearchModal
                    onClose={() => setManualCompanyModalOpen(false)}
                    onConfirm={handleManualCompanyConfirm}
                    onConfirmCompanies={handleManualCompanyConfirmCompanies}
                    enforcedCompanyEntityFilter="hasDataProviderId"
                />
            )}
        </>
    )
}

function CompanyTable({
    rows,
    onToggleRow,
    emptyLabel = 'No companies yet.',
    showMovers = false,
}: {
    rows: CompanyTableRow[]
    onToggleRow: (rowKey: string) => void
    emptyLabel?: string
    showMovers?: boolean
}): React.JSX.Element {
    return (
        <div className="border rounded-lg overflow-hidden bg-white">
            <table className="min-w-full text-xs">
                <thead className="bg-gray-100 text-gray-600">
                    <tr>
                        <th className="py-2 px-2 font-semibold text-left w-14">
                            Include
                        </th>
                        <th className="py-2 px-2 font-semibold text-left">
                            Company
                        </th>
                        <th className="py-2 px-2 font-semibold text-left">
                            Company ID
                        </th>
                        <th className="py-2 px-2 font-semibold text-left">
                            Data provider ID
                        </th>
                        {showMovers && (
                            <th className="py-2 px-2 font-semibold text-left">
                                Movers
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr className="border-t">
                            <td
                                colSpan={showMovers ? 5 : 4}
                                className="px-3 py-3 text-gray-500"
                            >
                                {emptyLabel}
                            </td>
                        </tr>
                    ) : (
                        rows.map((row) => (
                            <tr key={row.key} className="border-t">
                                <td className="py-2 px-2">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4"
                                        checked={row.included}
                                        onChange={() => onToggleRow(row.key)}
                                        aria-label={`Include ${row.companyName}`}
                                    />
                                </td>
                                <td className="py-2 px-2 text-gray-800">
                                    {row.companyName || 'Unknown company'}
                                </td>
                                <td className="py-2 px-2 text-gray-600">
                                    {row.companyId ?? '—'}
                                </td>
                                <td className="py-2 px-2 text-gray-600">
                                    {row.companyDataProviderId ?? '—'}
                                </td>
                                {showMovers && (
                                    <td className="py-2 px-2 text-gray-600">
                                        {typeof row.movers === 'number'
                                            ? row.movers.toLocaleString()
                                            : '—'}
                                    </td>
                                )}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    )
}

function buildCandidateSeedOption(person: PersonDto): CandidateSeedOption {
    const formattedName = formatPersonName({
        firstName: person.firstName,
        lastName: person.lastName,
    })

    return {
        personId: person.id,
        label: formattedName || person.linkedInUrl || person.id,
        headline: person.headline,
        linkedInUrl: person.linkedInUrl,
        location: person.location ?? person.locationCountry,
    }
}

function buildConnectionEntries(
    seedPersonId: string,
    resolvedPersonLites: ResolvedPersonLiteDto[],
    personMap: Map<string, PersonLiteDto>,
): ConnectionEntry[] {
    const entries: ConnectionEntry[] = []

    for (const resolved of resolvedPersonLites) {
        const person = personMap.get(resolved.personId)
        if (!person) continue

        const connectionDetail = resolved.connectionDetails.find(
            (detail) => detail.seedPersonId === seedPersonId,
        )

        if (!connectionDetail) continue

        entries.push({
            personId: resolved.personId,
            person,
            sharedWorkHistory: connectionDetail.sharedWorkHistory ?? [],
        })
    }

    return entries.sort(
        (a, b) =>
            (b.sharedWorkHistory?.length ?? 0) -
            (a.sharedWorkHistory?.length ?? 0),
    )
}

function extractActiveCompanyRoles(person: PersonDto): PersonCompanyRole[] {
    if (!person.workedAts || person.workedAts.length === 0) {
        return []
    }

    const activeRoles = person.workedAts.filter((workedAt) => !workedAt.endDate)
    const uniqueCompanies = new Map<string, PersonCompanyRole>()

    for (const workedAt of activeRoles) {
        const companyName = workedAt.company?.name ?? 'Unknown company'
        const key = workedAt.companyId ?? companyName.toLowerCase()
        if (!uniqueCompanies.has(key)) {
            uniqueCompanies.set(key, {
                companyId: workedAt.companyId,
                companyName,
                jobTitle: workedAt.jobTitle,
                companyDataProviderId: workedAt.company?.dataProviderId,
            })
        }
    }

    return Array.from(uniqueCompanies.values())
}

function parseJobTitlesInput(input: string): string[] {
    return input
        .split(/[\n,]/)
        .map((title) => title.trim())
        .filter((title) => Boolean(title))
}

function parseCompanyNamesInput(input: string): string[] {
    return dedupeStrings(
        input
            .split(/[\n,]/)
            .map((name) => name.trim())
            .filter((name) => name.length > 0),
    )
}

function parseLocationPairsInput(input: string): CoreSignalLocationPair[] {
    return input
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((line): CoreSignalLocationPair | null => {
            const commaIndex = line.indexOf(',')
            if (commaIndex === -1) {
                return null
            }
            const city = line.slice(0, commaIndex).trim()
            const country = line.slice(commaIndex + 1).trim()
            if (!city && !country) {
                return null
            }
            if (city && country) {
                return { city, country }
            }
            if (city) {
                return { city }
            }
            return { country }
        })
        .filter((pair): pair is CoreSignalLocationPair => pair !== null)
}

function parseSalaryInput(input: string): number | undefined {
    if (!input) {
        return undefined
    }
    const value = Number(input)
    if (!Number.isFinite(value)) {
        return undefined
    }
    return value
}

function dedupeJobTitles(titles: string[]): string[] {
    const seen = new Set<string>()
    const uniqueTitles: string[] = []

    titles.forEach((title) => {
        const trimmedTitle = title.trim()
        if (!trimmedTitle) {
            return
        }
        const normalized = trimmedTitle.toLowerCase()
        if (seen.has(normalized)) {
            return
        }
        seen.add(normalized)
        uniqueTitles.push(trimmedTitle)
    })

    return uniqueTitles
}

function dedupeStrings(values: string[]): string[] {
    const seen = new Set<string>()
    const uniqueValues: string[] = []

    values.forEach((value) => {
        if (!value) {
            return
        }
        if (seen.has(value)) {
            return
        }
        seen.add(value)
        uniqueValues.push(value)
    })

    return uniqueValues
}

function toggleCompanyRows(
    rows: CompanyTableRow[],
    rowKey: string,
): CompanyTableRow[] {
    return rows.map((row) =>
        row.key === rowKey ? { ...row, included: !row.included } : row,
    )
}

function buildResolvedCompanyRows(
    inputNames: string[],
    resolvedCompanyNames: Record<
        string,
        { id: string; name: string; dataProviderId?: string }[]
    >,
): CompanyTableRow[] {
    const rows: CompanyTableRow[] = []

    inputNames.forEach((inputName) => {
        const resolved = resolvedCompanyNames[inputName] ?? []
        resolved.forEach((company, index) => {
            rows.push({
                key: `${inputName}-${company.id}-${index}`,
                companyId: company.id,
                companyName: company.name || inputName,
                companyDataProviderId: company.dataProviderId,
                included: true,
            })
        })
    })

    return rows
}

function buildTalentFlowCompanyRows(
    results: TalentFlowResultDto[],
): CompanyTableRow[] {
    const rows = new Map<string, CompanyTableRow>()

    results.forEach((result) => {
        const companyId = result.destinationCompanyId
        const dataProviderId = result.destinationCompanyDataProviderId
        const key = dataProviderId ?? companyId
        if (!key) {
            return
        }

        const incomingName =
            result.destinationCompanyName?.trim() ??
            result.destinationCompanyId ??
            'Unknown company'
        const incomingMovers =
            typeof result.movers === 'number' ? result.movers : 0
        const existing = rows.get(key)

        if (existing) {
            rows.set(key, {
                ...existing,
                companyId: existing.companyId ?? companyId,
                companyName:
                    result.destinationCompanyName?.trim() ||
                    existing.companyName,
                companyDataProviderId:
                    existing.companyDataProviderId ??
                    dataProviderId ??
                    undefined,
                movers: (existing.movers ?? 0) + incomingMovers,
            })
            return
        }

        rows.set(key, {
            key,
            companyId,
            companyName: incomingName,
            companyDataProviderId: dataProviderId ?? undefined,
            movers: incomingMovers,
            included: true,
        })
    })

    return Array.from(rows.values()).sort((a, b) => {
        const moversA = a.movers ?? 0
        const moversB = b.movers ?? 0
        if (moversB !== moversA) {
            return moversB - moversA
        }
        return a.companyName.localeCompare(b.companyName)
    })
}

function mergeManualCompanyRows(
    existingRows: CompanyTableRow[],
    companies: CompanyCsSearchResultDto[],
): CompanyTableRow[] {
    const rowsByKey = new Map<string, CompanyTableRow>()

    existingRows.forEach((row) => {
        const key = row.companyDataProviderId ?? row.companyId ?? row.key
        if (key) {
            rowsByKey.set(key, row)
        }
    })

    companies.forEach((company) => {
        const dataProviderId = company.dataProviderId?.trim()
        const companyId = company.id?.trim()
        const key = dataProviderId ?? companyId
        if (!key) {
            return
        }

        const existing = rowsByKey.get(key)
        if (existing) {
            rowsByKey.set(key, {
                ...existing,
                companyName: company.name || existing.companyName,
                companyId: existing.companyId ?? companyId,
                companyDataProviderId:
                    existing.companyDataProviderId ?? dataProviderId,
            })
            return
        }

        rowsByKey.set(key, {
            key,
            companyId: companyId ?? undefined,
            companyName: company.name || companyId || 'Unknown company',
            companyDataProviderId: dataProviderId ?? undefined,
            included: true,
        })
    })

    return Array.from(rowsByKey.values()).sort((a, b) =>
        a.companyName.localeCompare(b.companyName),
    )
}

function parseDateValue(dateValue?: Date | string | null): Date | null {
    if (!dateValue) {
        return null
    }

    const parsed = dateValue instanceof Date ? dateValue : new Date(dateValue)
    if (Number.isNaN(parsed.getTime())) {
        return null
    }

    return parsed
}

function extractProfileJobTitles(person: PersonDto): string[] {
    const workedAts = person.workedAts ?? []
    const cutoffDate = new Date()
    cutoffDate.setFullYear(cutoffDate.getFullYear() - 3)
    const titles: string[] = []

    workedAts.forEach((workedAt) => {
        if (!workedAt.jobTitle) {
            return
        }
        const endDate = parseDateValue(workedAt.endDate)
        const isCurrentRole = !workedAt.endDate
        const isRecentRole = endDate ? endDate >= cutoffDate : false

        if (isCurrentRole || isRecentRole) {
            titles.push(workedAt.jobTitle)
        }
    })

    const currentTitle = person.currentJobDetails?.workedAt?.jobTitle
    if (currentTitle) {
        titles.push(currentTitle)
    }

    return dedupeJobTitles(titles)
}

function extractProfileCompanyRows(person: PersonDto): CompanyTableRow[] {
    const workedAts = person.workedAts ?? []
    const rows = new Map<string, CompanyTableRow>()

    workedAts.forEach((workedAt) => {
        const company = workedAt.company
        if (!company?.dataProviderId) {
            return
        }
        const key = company.dataProviderId
        if (rows.has(key)) {
            return
        }
        rows.set(key, {
            key,
            companyId: workedAt.companyId ?? company.id,
            companyName: company.name ?? 'Unknown company',
            companyDataProviderId: company.dataProviderId,
            included: true,
        })
    })

    const currentCompany = person.currentJobDetails?.company
    if (
        currentCompany?.dataProviderId &&
        !rows.has(currentCompany.dataProviderId)
    ) {
        rows.set(currentCompany.dataProviderId, {
            key: currentCompany.dataProviderId,
            companyId:
                person.currentJobDetails?.workedAt?.companyId ??
                currentCompany.id,
            companyName: currentCompany.name ?? 'Unknown company',
            companyDataProviderId: currentCompany.dataProviderId,
            included: true,
        })
    }

    return Array.from(rows.values()).sort((a, b) =>
        a.companyName.localeCompare(b.companyName),
    )
}

function getCurrentCompanyInfo(person: PersonLiteDto): {
    company: string
    jobTitle?: string
    dataProviderId?: string
} {
    const details = person.currentJobDetails
    if (!details) {
        return { company: 'Unknown company' }
    }

    if (details.variousCompanies) {
        return { company: 'Various companies' }
    }

    return {
        company: details.company?.name ?? 'Unknown company',
        jobTitle: details.workedAt?.jobTitle,
        dataProviderId:
            details.company?.dataProviderId ??
            details.workedAt?.company?.dataProviderId,
    }
}

function parseConnectionNamesInput(input: string): string[] {
    const rawEntries = input
        .split(/[,\n]/)
        .map((value) => value.trim())
        .filter((value) => value.length > 0)

    const seen = new Set<string>()
    const uniqueEntries: string[] = []

    rawEntries.forEach((value) => {
        const normalized = value.toLowerCase()
        if (seen.has(normalized)) {
            return
        }
        seen.add(normalized)
        uniqueEntries.push(value)
    })

    return uniqueEntries
}

function getPersonDisplayName(person: PersonLiteDto | PersonDto): string {
    return formatPersonName(person) || person.linkedInUrl || person.id
}

function extractJobDetailDescription(
    detail: CoreSignalJobDetail | null,
): string | null {
    if (!detail) {
        return null
    }

    const candidates = [
        detail.description,
        detail.job_description,
        detail?.job?.description,
    ]

    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim().length > 0) {
            return candidate
        }
    }

    return null
}

function normalizePreviewCompanyKey(companyName?: string): string {
    const trimmed = companyName?.trim()
    return trimmed ? trimmed.toLowerCase() : 'unknown company'
}

function getPreviewHighlightIds(
    results: CoreSignalJobPreviewResult[],
    highlightCount: number,
): Set<string> {
    if (results.length <= highlightCount) {
        return new Set(results.map((result) => result.id))
    }

    const selected = new Set<string>()
    const selectedCompanies = new Set<string>()

    for (const result of results) {
        if (selected.size >= highlightCount) {
            return selected
        }
        const companyKey = normalizePreviewCompanyKey(result.companyName)
        if (selectedCompanies.has(companyKey)) {
            continue
        }
        selected.add(result.id)
        selectedCompanies.add(companyKey)
    }

    for (const result of results) {
        if (selected.size >= highlightCount) {
            break
        }
        if (selected.has(result.id)) {
            continue
        }
        selected.add(result.id)
    }

    return selected
}
